import React from "react";
import { NodeProps, useReactFlow } from "reactflow";
import axios from "axios";
import { TextInput } from "../../../../form/textInput";
import { updateNodeValue } from "../../../../../services/method";
import { webURL } from "../../../../../services/API";
import { operationType, selectDataType } from "../../../../../services/types";
import { AuthenticateSelect } from "../../../../auth/select";
import { Select } from "../../../../form/select";
import { Main, Styled } from "./style";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";

type SupabaseProps = NodeProps<operationType> & {
    setChildMinimize?: (fn: (id: string) => void) => void;
    setChildSettings?: (fn: (id: string) => void) => void;
};

// Operation options
const getOperationOptions = (): selectDataType[] => {
    return [
        { id: 0, name: 'Let AI Agent Decide', refId: 'agent_decide' },
        { id: 1, name: 'Create a new Row', refId: 'create', isActive: true },
        { id: 2, name: 'Get all Rows', refId: 'read_list' },
        { id: 3, name: 'Get Row Details', refId: 'read_single' },
        { id: 4, name: 'Update Row', refId: 'update' },
        { id: 5, name: 'Delete Row', refId: 'delete' },
    ];
};

export function Supabase({ id, data, setChildMinimize, setChildSettings }: SupabaseProps) {
    const [operations, setOperations] = React.useState<selectDataType[]>(getOperationOptions());
    const [tables, setTables] = React.useState<selectDataType[]>([]);
    const [rows, setRows] = React.useState<selectDataType[]>([]);
    const [tableColumns, setTableColumns] = React.useState<{ name: string; type: string }[]>([]);

    const { setNodes } = useReactFlow();
    const conn = data.value?.connection;
    // const credential = conn ? `projectUrl=${encodeURIComponent(conn.projectUrl)}&apiKey=${encodeURIComponent(conn.apiKey)}` : '';
    const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
    const [isExpanded, setIsExpanded] = React.useState(false);
    const [isLoadingTables, setIsLoadingTables] = React.useState(false);
    const [isLoadingRows, setIsLoadingRows] = React.useState(false);

    // --
    React.useEffect(() => {
        if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
        if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [setChildMinimize, setChildSettings]);

    // Initialize operation
    React.useEffect(() => {
        if (!data.value) {
            updateNodeValue(id, setNodes, { operation: 'create' });
        } else {
            const currentOperation = data.value.operation || 'create';
            setOperations(ops => ops.map(o => ({ ...o, isActive: o.refId === currentOperation })));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Fetch tables when component loads with existing connection
    React.useEffect(() => {
        if (conn?.projectUrl && conn?.apiKey && tables.length === 0 && isExpanded) {
            fetchTables(conn);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [conn, isExpanded]);

    // Handle connection authentication
    const handleAuthenticate = async (type: 'add' | 'remove', connectionData: any) => {
        if (type === 'add') {
            updateNodeValue(id, setNodes, { connection: connectionData });
            // Fetch tables after connection
            await fetchTables(connectionData);
        } else if (type === 'remove') {
            updateNodeValue(id, setNodes, { connection: null, tables: [], rows: [], fields: {} });
            setTables([]);
            setRows([]);
        }
    };

    // Fetch available tables
    const fetchTables = async (connData?: any) => {
        const c = connData || conn;
        if (!c?.projectUrl || !c?.apiKey) return;

        setIsLoadingTables(true);

        try {
            const result = (await axios.post(`${webURL}/crm/supabase/tables`, { projectUrl: c.projectUrl, apiKey: c.apiKey })).data;
            const tablesList = result.map((item: any, index: number) => ({
                id: index + 1,
                name: item.name,
                refId: item.name,
                isActive: data.value?.table === item.name || index === 0
            }));
            setTables(tablesList);

            // Auto-select first table if none selected
            if (tablesList.length > 0 && !data.value?.table) {
                const firstTable = tablesList[0];
                updateNodeValue(id, setNodes, { table: firstTable.refId });
                await fetchTableColumns(firstTable.refId, c);

                // Fetch rows for update/delete operations
                const currentOp = data.value?.operation || 'create';
                if (['read_single', 'update', 'delete'].includes(currentOp)) {
                    await fetchRows(firstTable.refId, c);
                }
            } else if (data.value?.table) {
                await fetchTableColumns(data.value.table, c);
                const currentOp = data.value?.operation || 'create';
                if (['read_single', 'update', 'delete'].includes(currentOp)) {
                    await fetchRows(data.value.table, c);
                }
            }
        } catch (err) {
            console.error('Failed to fetch tables:', err);
            setTables([]);
        }
        setIsLoadingTables(false);
    };

    // Fetch table columns
    const fetchTableColumns = async (tableName: string, connData?: any) => {
        const c = connData || conn;
        if (!c?.projectUrl || !c?.apiKey || !tableName) return;

        try {
            const result = (await axios.post(`${webURL}/crm/supabase/tables/${tableName}/columns`, { projectUrl: c.projectUrl, apiKey: c.apiKey })).data;
            setTableColumns(result || []);
        } catch (err) {
            console.error('Failed to fetch columns:', err);
            setTableColumns([]);
        }
    };

    // Fetch rows for the current table
    const fetchRows = async (tableName?: string, connData?: any) => {
        const c = connData || conn;
        const tbl = tableName || data.value?.table;
        if (!c?.projectUrl || !c?.apiKey || !tbl) return;

        setIsLoadingRows(true);

        try {
            const result = (await axios.post(`${webURL}/crm/supabase/${tbl}`, { projectUrl: c.projectUrl, apiKey: c.apiKey, limit: 100 })).data;
            const rowsList = result.map((item: any, index: number) => {
                // Try to get a meaningful name from common field names
                const displayName = item.name || item.title || item.email ||
                    item.first_name || item.last_name ||
                    // Fallback: get first string value from fields
                    Object.values(item).find(v => typeof v === 'string' && v.length > 0) ||
                    item.id;

                return {
                    id: index + 1,
                    name: String(displayName).substring(0, 50),
                    refId: item.id,
                    fields: item,
                    isActive: index === 0
                };
            });
            setRows(rowsList);
            updateNodeValue(id, setNodes, { rows: rowsList });
        } catch (err) {
            console.error('Failed to fetch rows:', err);
            setRows([]);
        }
        setIsLoadingRows(false);
    };

    // Handle input changes
    const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>, keyRef?: string, isFields?: boolean) => {
        if (keyRef) {
            if (isFields) {
                setNodes(nodes => nodes.map(node => {
                    if (node.id === id) return { ...node, data: { ...node.data, value: { ...node.data.value, fields: { ...node.data.value.fields, [keyRef]: e.target.value } } } }
                    else return node;
                }));
            } else {
                updateNodeValue(id, setNodes, { [keyRef]: e.target.value });
            }
        }
    };

    // Handle table selection
    const handleSelectTable = async (selectId: number) => {
        const selected = tables.find(t => t.id === selectId);
        if (!selected) return;

        setTables(tables => tables.map(t => ({ ...t, isActive: t.id === selectId })));
        updateNodeValue(id, setNodes, { table: selected.refId, rows: [], fields: {} });
        setRows([]);

        await fetchTableColumns(selected.refId as string);

        // Fetch rows for update/delete operations
        const currentOp = data.value?.operation || 'create';
        if (['read_single', 'update', 'delete'].includes(currentOp)) {
            await fetchRows(selected.refId as string);
        }
    };

    // Handle operation selection
    const handleSelectOperation = async (selectId: number) => {
        const refId = operations.find(o => o.id === selectId)?.refId || 'create';
        setOperations(operations => operations.map(o => ({ ...o, isActive: o.id === selectId })));
        updateNodeValue(id, setNodes, { operation: refId });

        // Refresh rows for read/update/delete operations
        if (['read_single', 'update', 'delete'].includes(refId) && conn && data.value?.table) {
            await fetchRows();
        }
    };

    // Handle row selection
    const handleSelectRow = async (selectId: number) => {
        const selected = rows.find(r => r.id === selectId);
        if (!selected) return;

        setRows(recs => recs.map(r => ({ ...r, isActive: r.id === selectId })));

        // Populate fields with row data for update
        if (data.value?.operation === 'update' && (selected as any).fields) {
            // Filter out id column
            const editableFields: Record<string, any> = {};
            for (const [key, value] of Object.entries((selected as any).fields)) {
                if (key !== 'id' && key !== 'created_at' && key !== 'updated_at') {
                    editableFields[key] = value;
                }
            }

            updateNodeValue(id, setNodes, {
                rows: rows.map(r => ({ ...r, isActive: r.id === selectId })),
                fields: editableFields
            });
        } else {
            updateNodeValue(id, setNodes, { rows: rows.map(r => ({ ...r, isActive: r.id === selectId })) });
        }
    };



    // Computed values
    const currentOperation = data.value?.operation || 'create';
    const isAgentDecide = currentOperation === 'agent_decide';
    const isWarning = !data.value?.connection;
    const showRowSelector = !isAgentDecide && ['read_single', 'update', 'delete'].includes(currentOperation);
    const showFormFields = !isAgentDecide && ['create', 'update'].includes(currentOperation);

    // Render dynamic form fields based on table schema
    const renderFormFields = () => {
        // If we have columns from schema, use those; otherwise show generic fields
        const fieldsToShow = tableColumns.length > 0
            ? tableColumns.filter(c => c.name !== 'id' && c.name !== 'created_at' && c.name !== 'updated_at')
            : [];

        if (fieldsToShow.length === 0) {
            return null;
        }

        return (
            <div className="scroll nowheel" style={{ marginTop: '20px' }}>
                {fieldsToShow.slice(0, 10).map((field) => (
                    <TextInput
                        key={field.name}
                        keyRef={field.name}
                        title={field.name}
                        placeholder={`Enter ${field.name}`}
                        value={data.value?.fields?.[field.name] || ''}
                        handleChange={(e, keyRef) => handleChangeInput(e, keyRef, true)}
                    />
                ))}
                {fieldsToShow.length > 10 && (
                    <p style={{ color: '#888', fontSize: '0.75rem', padding: '5px 0' }}>
                        +{fieldsToShow.length - 10} more fields (use dynamic input)
                    </p>
                )}
            </div>
        );
    };

    return (<>
        <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
        <Main>
            {isWarning ? <>
                <div id="warning"></div>
                <span id="warningIcon"><IoWarningOutline size={21} /></span>
            </> : data.output ? <>
                <div id="ran"></div>
                <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
            </> : null}
            {isExpanded ? <ExpandedBlock
                data={{ id, ...data }}
                handleClose={() => setIsExpanded(false)}
                isWarning={isWarning}
                style={{ width: '420px' }}
            ><Styled>
                    <AuthenticateSelect type="supabase" defaultId={data.value?.connection?.id} handleAuthenticate={handleAuthenticate} background={data.background} withLine />

                    {/* Table Select */}
                    <Select
                        title={isLoadingTables ? "Loading Tables..." : "Table"}
                        data={tables}
                        handleSelectItem={(selectId) => handleSelectTable(selectId)}
                        isSmall={true}
                        emptyMsg="Connect to load tables"
                    />

                    {/* Operation Select */}
                    <Select title="Operation" data={operations} handleSelectItem={(selectId) => handleSelectOperation(selectId)} isSmall={true} />



                    {/* Operation-specific config — hidden when AI decides */}
                    {!isAgentDecide && (<>
                        {/* Row ID Input (for read_single, update, delete) */}
                        {showRowSelector && (
                            <TextInput
                                keyRef="rowId"
                                title="Row ID"
                                placeholder="Enter the row ID"
                                value={data.value?.rowId || ''}
                                handleChange={(e, keyRef) => handleChangeInput(e, keyRef)}
                            />
                        )}

                        {/* Filter input (for read_list) */}
                        {currentOperation === 'read_list' && (
                            <TextInput
                                keyRef="filter"
                                title="Filter"
                                placeholder="name=eq.John"
                                value={data.value?.filter || ''}
                                handleChange={(e, keyRef) => handleChangeInput(e, keyRef)}
                            />
                        )}

                        {/* Form Fields (for create, update) */}
                        {showFormFields && renderFormFields()}
                    </>)}

                </Styled></ExpandedBlock> : null}
        </Main>
    </>);
}